import { Button, ButtonGroup, DateUtil, Input, labelMedium, SearchBolder, TableColumn, TableRow, Tooltip } from '@aon/aon-tws-common-ui';
import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import HtmlParser from 'html-react-parser';
import { User } from 'oidc-client';
import * as React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import { IColumn, ISurveyListItem, IValidationResult } from '../../models';
import { SurveyStatusType } from '../../models/SurveyStatusType';
import { ColumnHelper, SurveyHelper } from '../../utilities';
import { TableLinkWrapper, WarningModal } from '../common';
import { ActionMenu } from './ActionMenu';

const ButtonContainer = styled.div`
	border-top: ${globalVars.border1} solid #cccccc;
	padding: ${globalVars.spacing20} ${globalVars.spacing0};
	width: ${globalVars.fullWidth};
	text-align: right;
`;
const FormContainer = styled.div`
	margin: 20px 40px;
	text-align: left;
	@media (min-width: 0) and (max-width: 768px) {
		margin: 20px 0;
	}
`;
const FormRow = styled.div`
	margin-bottom: 20px;
	&:last-child {
		margin-bottom: 30px;
	}
	@media (min-width: 0) and (max-width: 768px) {
		.tws-input-text {
			max-width: 100%;
		}
	}
`;
const Title = styled.span`
	margin-right: 30px;
	${labelMedium};
	@media (min-width: 0) and (max-width: 768px) {
		display: block;
		margin-bottom: 8px;
	}
`;

const WarningToolTipWrapper = styled.div`
	span{
		a{
			margin-right: 0px !important
			color:white !important;
			font-weight: bold;
		}
		i{
			color: red !important;
		}
	}
`;
WarningToolTipWrapper.displayName = 'WarningToolTipWrapper';

const NameContainer = styled.div`
	.child {
		display: inline-block;
	}
`;
NameContainer.displayName = 'NameContainer';
const WarningContainer = styled.div`
	display: inline-block;
	i {
		color: red !important;
	}
`;
WarningContainer.displayName = 'WarningContainer';

export interface ISurveyRowType {
	key: number | string | undefined;
	survey: ISurveyListItem;
	validationResults?: IValidationResult;
	columns: IColumn[];
	searchText?: string;
	user?: User;
	CloneSurvey: (surveyId: number) => void;
	GoToSurvey: (surveyId: number, surveyStatus: SurveyStatusType) => void;
	GoToSurveyAdmin: (surveyId: number) => void;
	ClearValidation: (itemToClear: ISurveyListItem) => void;
	UpdateSurveyName: (survey: ISurveyListItem) => any;
	ToggleSurveyCopyModal: () => any;
	ToggleSurveyDeleteModal: () => void;
	EditSurveyTemplate: (surveyId: number) => void;
}

export interface ISurveyRowState {
	expanded: boolean;
	editedSurveyName: string;
	canClose: boolean;
	isSurveyTemplateWarningModalOpen: boolean;
}

export class SurveyRow extends React.Component<ISurveyRowType, ISurveyRowState> {
	constructor(props: ISurveyRowType) {
		super(props);

		this.state = {
			expanded: false,
			editedSurveyName: this.props.survey.name,
			canClose: false,
			isSurveyTemplateWarningModalOpen: false,
		};

		this.toggleExpanded = this.toggleExpanded.bind(this);
		this.handleInputOnChange = this.handleInputOnChange.bind(this);
		this.handleSurveyNameUpdate = this.handleSurveyNameUpdate.bind(this);
		this.handleUpdateAndClose = this.handleUpdateAndClose.bind(this);
	}

	public UNSAFE_componentWillUpdate() {
		const isValid = this.props.validationResults ? this.props.validationResults.isValid : true;
		if (this.state.canClose) {
			this.setState({ canClose: false });
			if (isValid) {
				this.toggleExpanded(false);
			}
		}
	}

	public render() {
		const visibleColumns = ColumnHelper.GetSelectedColumns(this.props.columns);
		return (
			<>
				<TableRow expanded={this.state.expanded} expandedRowRender={() => this.getEditExpandableRowRender()} record={{}}>
					{visibleColumns.map((column: IColumn) => this.getQuestionPropertyText(column))}
				</TableRow>
				<WarningModal
					isOpen={this.state.isSurveyTemplateWarningModalOpen}
					contentHeadline={ContentConstants.WARNING_MODAL_SURVEY_TEMPLATE_EDIT_HEADLINE}
					contentBody={ContentConstants.WARNING_MODAL_SURVEY_TEMPLATE_EDIT_BODY}
					onClose={() => this.setState({ isSurveyTemplateWarningModalOpen: false })}
					onSubmit={() => this.handleConfirmTemplateWarningModal()}
				/>
			</>
		);
	}

	public toggleExpanded(shouldExpand: boolean) {
		const nextState = {
			expanded: shouldExpand,
			editedSurveyName: !shouldExpand ? this.props.survey.name : this.state.editedSurveyName,
		};

		this.setState(nextState, () => {
			if (!shouldExpand) {
				this.props.ClearValidation(this.props.survey);
			}
		});
	}

	public handleInputOnChange(e: any) {
		this.setState({ editedSurveyName: e.target.value });
	}

	public handleSurveyNameUpdate() {
		this.props.UpdateSurveyName({ ...this.props.survey, name: this.state.editedSurveyName });
	}

	public handleUpdateAndClose() {
		this.handleSurveyNameUpdate();
		this.setState({ canClose: true });
	}

	private getEditExpandableRowRender() {
		const visibleColumns = ColumnHelper.GetSelectedColumns(this.props.columns);
		const messages = this.props.validationResults ? this.props.validationResults.errors : [];
		const isValid = this.props.validationResults ? this.props.validationResults.isValid : true;
		return (
			<TableColumn colSpan={visibleColumns.length}>
				<FormContainer>
					<FormRow>
						<Title>{ContentConstants.INPUTLABEL_RENAME_SURVEY}</Title>
						<Input
							placeholder={this.props.survey.name}
							value={this.state.editedSurveyName}
							onChange={this.handleInputOnChange}
							error={!isValid}
							message={messages}
							maxLength={150}
							id='survey-name'
						/>
					</FormRow>
				</FormContainer>
				<ButtonContainer>
					<ButtonGroup type='column'>
						<Button id='CloseButton' size='medium' color='secondary' onClick={() => this.toggleExpanded(false)}>
							{ContentConstants.BUTTONS_CLOSE}
						</Button>
						<Button id='UpdateCloseButton' size='medium' color='primary' onClick={this.handleUpdateAndClose}>
							{ContentConstants.BUTTONS_UPDATE_AND_CLOSE}
						</Button>
						<Button id='UpdateButton' size='medium' color='primary' onClick={this.handleSurveyNameUpdate}>
							{ContentConstants.BUTTONS_UPDATE}
						</Button>
					</ButtonGroup>
				</ButtonContainer>
			</TableColumn>
		);
	}

	private handleNameClick() {
		const status = this.props.survey.surveyStatus;
		if (status === SurveyStatusType.PublicSurveyTemplate) {
			this.setState({ isSurveyTemplateWarningModalOpen: true });
		} else {
			this.props.GoToSurvey(this.props.survey.surveyId, status);
		}
	}

	private handleConfirmTemplateWarningModal() {
		this.props.EditSurveyTemplate(this.props.survey.surveyId);
	}

	private getQuestionPropertyText(column: IColumn) {
		const key = column.name.toLowerCase();
		const isSurveyProcessing =
			this.props.survey.surveyStatus === SurveyStatusType.SchedulingInProgress ||
			this.props.survey.surveyStatus === SurveyStatusType.ClosingInProgress;
		const hasBeenCancelled = this.props.survey.surveyStatus === SurveyStatusType.Cancelled;
		const hasSurveyFailed =
			this.props.survey.surveyStatus === SurveyStatusType.Failed || this.props.survey.surveyStatus === SurveyStatusType.SurveyCloseFailed;
		const surveyFailureText =
			this.props.survey.surveyStatus === SurveyStatusType.Failed
				? ContentConstants.INSTRUCTIONALTEXT_SURVEY_PUBLISH_FAILURE
				: ContentConstants.INSTRUCTIONALTEXT_SURVEY_CLOSE_FAILURE;
		const showPendingParticipantsWarning =
			this.props.survey.hasPendingContact &&
			(this.props.survey.surveyStatus === SurveyStatusType.SchedulingInProgress ||
				this.props.survey.surveyStatus === SurveyStatusType.Scheduled ||
				this.props.survey.surveyStatus === SurveyStatusType.SurveyInProgress);
		let content;
		switch (column.name) {
			case 'Name': {
				content = (
					<NameContainer>
						<TableLinkWrapper onClick={() => this.handleNameClick()} disabled={isSurveyProcessing}>
							<SearchBolder text={this.props.survey.name} searchText={this.props.searchText} />
							{showPendingParticipantsWarning && (
								<WarningContainer>
									<Tooltip id={'pending-participants-message-tooltip_' + this.props.survey.surveyId} iconName='warning'>
										{ContentConstants.INSTRUCTIONALTEXT_PENDING_PARTICIPANTS_WARNING}
									</Tooltip>
								</WarningContainer>
							)}
						</TableLinkWrapper>
					</NameContainer>
				);
				break;
			}
			case 'Program':
				content = 'Custom';
				break;
			case 'Status': {
				const status = SurveyHelper.getSurveyStatusName(this.props.survey.surveyStatus as SurveyStatusType);
				content = hasSurveyFailed ? (
					<WarningToolTipWrapper className='tws-warning-tooltip-wrapper'>
						{' '}
						{status}
						<Tooltip id={'survey-publish-failed-message-tooltip_' + this.props.survey.surveyId} iconName='warning'>
							{HtmlParser(surveyFailureText)}
							<br />
							<br />
							<strong>Reason:</strong> {this.props.survey.message}
						</Tooltip>
					</WarningToolTipWrapper>
				) : (
					status
				);
				break;
			}
			case 'Create Date':
				content = DateUtil.formatDateToLocal(this.props.survey.createdOn);
				break;
			case 'Modified Date':
				content = DateUtil.formatDateToLocal(this.props.survey.modifiedOn);
				break;
			case 'Scheduled Date':
				content = this.props.survey.publishedOn === null ? '' : DateUtil.formatDateToLocal(this.props.survey.publishedOn);
				break;
			case 'Actions':
				content = isSurveyProcessing ? (
					''
				) : (
					<ActionMenu
						CloneSurvey={this.props.CloneSurvey}
						GoToSurveyAdmin={this.props.GoToSurveyAdmin}
						Expand={this.toggleExpanded}
						survey={this.props.survey}
						OpenCopySurveyModal={this.props.ToggleSurveyCopyModal}
						OpenDeleteSurveyModal={this.props.ToggleSurveyDeleteModal}
						user={this.props.user}
					/>
				);
				break;
			case 'Open Date':
				content = !hasBeenCancelled
					? DateUtil.formatDateToLocal(this.props.survey.openDate)
					: ContentConstants.SURVEY_ADMIN_PROGRESS_DASHDASH;
				break;
			case 'Close Date':
				content = !hasBeenCancelled
					? DateUtil.formatDateToLocal(this.props.survey.closeDate)
					: ContentConstants.SURVEY_ADMIN_PROGRESS_DASHDASH;
				break;
			case 'Owner':
				content = this.props.survey.owner;
				break;
			default:
				return <TableColumn key={key} />;
		}
		return <TableColumn key={key}>{content}</TableColumn>;
	}
}
